// /////////////////////////////////////////////////////////////
// HOME デザイン試作ページ (/home2)
//
// 新しい HOME 画面のデザイン案を並べて試すための管理者専用ページ。
// 専用 permission (HOME_DESIGN_LAB_PERMISSION) を持つユーザーだけが閲覧できる。
// 案を追加する場合は DESIGN_VARIANTS へ 1 行足し、描画分岐を追加する。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useMemo, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { usePermissions, NavigationMenuType } from '../firebase/PermissionProvider';
import LoadingView from '../components/LoadingView';
import OrbitMenuHome, { OrbitMenuItem } from '../components/homeDesigns/OrbitMenuHome';
import { PAGE_FILL_SX } from '../constants/layout';
import * as Icons from '../constants/icons';
import { MENU_CATEGORIES } from '../constants/masterData';

// 本ページの閲覧権限 (開発段階は masaya_taira のみに付与 rules.md §7)
export const HOME_DESIGN_LAB_PERMISSION = 'menu.sub.homedesignlab.view';

type DesignVariant = 'orbit' | 'category' | 'neural' | 'particle';

const DESIGN_VARIANTS: { value: DesignVariant; label: string }[] = [
  { value: 'orbit', label: '案1 オービット' },
  { value: 'category', label: '案2 カテゴリ' },
  { value: 'neural', label: '案3 ニューラル' },
  { value: 'particle', label: '案4 パーティクル' },
];

// 案2 で円周に並べるカテゴリのアイコン。MENU_CATEGORIES に無いもの (sort_no 0〜1 台) は共通扱い
const COMMON_CATEGORY_ID = '0';
const CATEGORY_ICONS: Record<string, string> = {
  [COMMON_CATEGORY_ID]: 'Home',
  '2': 'Handshake',
  '3': 'Assessment',
  '6': 'AccountBalanceWallet',
  '8': 'Storage',
  '9': 'Code',
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 11) return 'おはようございます';
  if (hour >= 11 && hour < 18) return 'こんにちは';
  return 'こんばんは';
};

// Firestore の child は配列またはオブジェクトで入っているため配列へ正規化する
// (NavigationBar.tsx と同じ扱い)
const normalizeChildren = (menu: NavigationMenuType): NavigationMenuType[] => {
  if (!menu.child) return [];
  if (Array.isArray(menu.child)) return menu.child.filter((c) => c !== null);
  if (typeof menu.child === 'object') {
    return Object.values(menu.child).filter((c) => c !== null) as NavigationMenuType[];
  }
  return [];
};

export default function HomeDesignLab() {
  const { permissionRolesData, permissionData, loading } = usePermissions();
  const [variant, setVariant] = useState<DesignVariant>('orbit');
  const [greeting, setGreeting] = useState(getGreeting());

  useEffect(() => {
    const timer = setInterval(() => setGreeting(getGreeting()), 60000);
    return () => clearInterval(timer);
  }, []);

  // サイドメニューと同じ条件 (ユーザーが permission を持つ項目のみ) で親メニューを抽出
  const menuItems = useMemo<OrbitMenuItem[]>(
    () =>
      permissionRolesData
        .filter((menu) => menu && menu.permission && permissionData.includes(menu.permission))
        .map((menu) => ({
          menu,
          children: normalizeChildren(menu).filter(
            (c) => c.permission && permissionData.includes(c.permission),
          ),
        }))
        // 遷移先も子メニューも無い項目は押しても何も起きないため除外
        .filter((item) => item.menu.path || item.children.length > 0),
    [permissionRolesData, permissionData],
  );

  // 案2 用 親メニューを sort_no の整数部 (カテゴリ) ごとにまとめ、カテゴリを円周の項目にする。
  // カードには各メニューを並べ、メニューの子項目は child に入れて字下げ表示させる
  const categoryItems = useMemo<OrbitMenuItem[]>(() => {
    const groups = new Map<string, NavigationMenuType[]>();
    menuItems.forEach(({ menu, children }) => {
      const rawId = Math.floor(menu.sort_no).toString();
      const categoryId = MENU_CATEGORIES[rawId] ? rawId : COMMON_CATEGORY_ID;
      const list = groups.get(categoryId) ?? [];
      list.push({ ...menu, child: children });
      groups.set(categoryId, list);
    });
    return Array.from(groups.entries()).map(([categoryId, menus]) => ({
      menu: {
        id: `category-${categoryId}`,
        path: '',
        icon: CATEGORY_ICONS[categoryId] ?? 'Category',
        label: MENU_CATEGORIES[categoryId] ?? '共通メニュー',
        permission: '',
        sort_no: Number(categoryId),
      },
      children: menus,
    }));
  }, [menuItems]);

  if (loading) return <LoadingView message="読み込み中..." height="80vh" />;

  if (!permissionData.includes(HOME_DESIGN_LAB_PERMISSION)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 12, gap: 1.5 }}>
        <Icons.Lock sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography color="text.secondary">このページを表示する権限がありません。</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 1.5, width: '96%', margin: '0 auto', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>HOME デザイン試作</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={variant}
          onChange={(_, v: DesignVariant | null) => v && setVariant(v)}
        >
          {DESIGN_VARIANTS.map((d) => (
            <ToggleButton key={d.value} value={d.value} sx={{ px: 1.5, py: 0.25, fontSize: '0.78rem' }}>
              {d.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          管理者のみ閲覧できる試作ページです。現行の HOME (/) には影響しません。
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 520, pb: 1 }}>
        {variant === 'orbit' && <OrbitMenuHome items={menuItems} greeting={greeting} />}
        {variant === 'category' && (
          <OrbitMenuHome items={categoryItems} greeting={greeting} showLegend={false} />
        )}
        {/* 案3・案4 は中央を Three.js 演出にし、メニューは項目数が少なく流れが見やすいカテゴリ単位で並べる */}
        {variant === 'neural' && (
          <OrbitMenuHome items={categoryItems} greeting={greeting} showLegend={false} centerEffect="neural" />
        )}
        {variant === 'particle' && (
          <OrbitMenuHome items={categoryItems} greeting={greeting} showLegend={false} centerEffect="particle" />
        )}
      </Box>
    </Box>
  );
}
